
import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useTranslation } from "react-i18next";

interface TermsAcceptanceProps {
  termsAccepted: boolean;
  privacyAccepted: boolean;
  marketingAccepted: boolean;
  onTermsChange: (accepted: boolean) => void;
  onPrivacyChange: (accepted: boolean) => void;
  onMarketingChange: (accepted: boolean) => void;
  termsError?: string;
  privacyError?: string;
}

export const TermsAcceptance = ({
  termsAccepted,
  privacyAccepted,
  marketingAccepted,
  onTermsChange,
  onPrivacyChange,
  onMarketingChange,
  termsError,
  privacyError
}: TermsAcceptanceProps) => {
  const { t, i18n } = useTranslation(['auth', 'common']);

  // Determine language-specific URLs
  const isEnglish = i18n.language === 'en';
  const termsUrl = isEnglish ? '/terms-en' : '/terms';
  const privacyUrl = isEnglish ? '/privacy-en' : '/privacy';

  // Create link components for interpolation
  const termsLink = (
    <a
      href={termsUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="text-uplivion-blue hover:underline font-medium"
    >
      {t('common:legal.termsOfService')}
    </a>
  );

  const privacyLink = (
    <a
      href={privacyUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="text-uplivion-blue hover:underline font-medium"
    >
      {t('common:legal.privacyPolicy')}
    </a>
  );

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div className="flex items-start space-x-3">
          <Checkbox
            id="terms"
            checked={termsAccepted}
            onCheckedChange={onTermsChange}
            className={termsError ? "border-red-500" : ""}
          />
          <div className="space-y-1 leading-none">
            <Label
              htmlFor="terms"
              className={`text-sm cursor-pointer ${termsError ? "text-red-600" : ""}`}
            >
              {isEnglish ? (
                <>I agree to the {termsLink} *</>
              ) : (
                <>Ich stimme den {termsLink} zu. *</>
              )}
            </Label>
            {termsError && (
              <p className="text-sm text-red-600">{termsError}</p>
            )}
          </div>
        </div>

        <div className="flex items-start space-x-3">
          <Checkbox
            id="privacy"
            checked={privacyAccepted}
            onCheckedChange={onPrivacyChange}
            className={privacyError ? "border-red-500" : ""}
          />
          <div className="space-y-1 leading-none">
            <Label
              htmlFor="privacy"
              className={`text-sm cursor-pointer ${privacyError ? "text-red-600" : ""}`}
            >
              {isEnglish ? (
                <>I have read and accept the {privacyLink} *</>
              ) : (
                <>Ich habe die {privacyLink} gelesen und akzeptiert. *</>
              )}
            </Label>
            {privacyError && (
              <p className="text-sm text-red-600">{privacyError}</p>
            )}
          </div>
        </div>

        <div className="flex items-start space-x-3">
          <Checkbox
            id="marketing"
            checked={marketingAccepted}
            onCheckedChange={onMarketingChange}
          />
          <div className="space-y-1 leading-none">
            <Label
              htmlFor="marketing"
              className="text-sm cursor-pointer"
            >
              {t('common:legal.marketingConsent')}
            </Label>
          </div>
        </div>
      </div>

      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-orange-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-orange-800">
              {t('common:legal.betaNoticeTitle')}
            </h3>
            <p className="mt-1 text-sm text-orange-700">
              {t('common:legal.betaNoticeText')}
            </p>
          </div>
        </div>
      </div>

      <p className="text-xs text-gray-500">
        {t('common:legal.requiredFields')}
      </p>
    </div>
  );
};
